import { loadStripe, Stripe } from '@stripe/stripe-js'
import { supabase } from '@/lib/supabaseClient'

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined

let stripePromise: Promise<Stripe | null>

const getStripe = () => {
  if (!stripePromise && stripePublishableKey) {
    stripePromise = loadStripe(stripePublishableKey)
  }
  return stripePromise
}

// Pricing tiers
export const PRICING_TIERS = {
  trial: {
    id: 'trial',
    name: 'Trial',
    description: '14-day free trial',
    price: 0,
    priceId: null,
    features: [
      '10 submissions',
      '1 user',
      'Basic AI extraction',
      'Email support'
    ],
    limits: {
      submissions: 10,
      users: 1
    }
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'For growing teams',
    price: 99,
    priceId: import.meta.env.VITE_STRIPE_PRO_PRICE_ID as string || 'price_pro',
    features: [
      'Unlimited submissions',
      'Up to 5 users',
      'Advanced AI extraction',
      'Priority support',
      'Custom integrations'
    ],
    limits: {
      submissions: -1, // unlimited
      users: 5
    }
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations',
    price: 299,
    priceId: import.meta.env.VITE_STRIPE_ENTERPRISE_PRICE_ID as string || 'price_enterprise',
    features: [
      'Unlimited submissions',
      'Unlimited users',
      'Advanced AI extraction',
      'SSO/SAML',
      'Priority support',
      'Custom integrations',
      'Dedicated account manager',
      'SLA guarantees'
    ],
    limits: {
      submissions: -1,
      users: -1
    }
  }
} as const

export type PricingTier = keyof typeof PRICING_TIERS

export interface SubscriptionData {
  status: 'trialing' | 'active' | 'canceled' | 'incomplete' | 'past_due' | 'unpaid' | null
  tier: PricingTier
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  trialEnd: string | null
}

class StripeService {
  /**
   * Create a Stripe Checkout session for subscription
   */
  async createCheckoutSession(priceId: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke('stripe-webhook', {
      body: {
        action: 'create-checkout',
        priceId
      }
    })

    if (error) {
      console.error('Error creating checkout session:', error)
      throw new Error('Failed to create checkout session')
    }

    return data.url
  }

  /**
   * Redirect to Stripe Checkout
   */
  async redirectToCheckout(priceId: string): Promise<void> {
    const checkoutUrl = await this.createCheckoutSession(priceId)
    window.location.href = checkoutUrl
  }

  /**
   * Create a Stripe Customer Portal session
   */
  async createPortalSession(): Promise<string> {
    const { data, error } = await supabase.functions.invoke('stripe-webhook', {
      body: { action: 'create-portal' }
    })

    if (error) {
      console.error('Error creating portal session:', error)
      throw new Error('Failed to create portal session')
    }

    return data.url
  }

  /**
   * Redirect to Stripe Customer Portal
   */
  async redirectToPortal(): Promise<void> {
    const portalUrl = await this.createPortalSession()
    window.location.href = portalUrl
  }

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus(): Promise<SubscriptionData> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return {
        status: null,
        tier: 'trial',
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        trialEnd: null
      }
    }

    // Get user's organization
    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!profile?.organization_id) {
      return {
        status: null,
        tier: 'trial',
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        trialEnd: null
      }
    }

    // Get subscription
    const { data: subscription } = await supabase
      .from('stripe_subscriptions')
      .select('*')
      .eq('organization_id', profile.organization_id)
      .single()

    if (!subscription) {
      return {
        status: 'trialing',
        tier: 'trial',
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        trialEnd: null
      }
    }

    // Determine tier from price ID
    let tier: PricingTier = 'trial'
    if (subscription.stripe_price_id === PRICING_TIERS.pro.priceId) {
      tier = 'pro'
    } else if (subscription.stripe_price_id === PRICING_TIERS.enterprise.priceId) {
      tier = 'enterprise'
    }

    return {
      status: subscription.status,
      tier,
      currentPeriodEnd: subscription.current_period_end,
      cancelAtPeriodEnd: subscription.cancel_at_period_end,
      trialEnd: subscription.trial_end
    }
  }

  /**
   * Get invoices for the organization
   */
  async getInvoices(limit = 10): Promise<Array<{
    id: string
    amount: number
    status: string
    createdAt: string
    invoiceUrl: string | null
    invoicePdf: string | null
  }>> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []

    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!profile?.organization_id) return []

    const { data: invoices } = await supabase
      .from('stripe_invoices')
      .select('*')
      .eq('organization_id', profile.organization_id)
      .order('created_at', { ascending: false })
      .limit(limit)

    return (invoices || []).map((inv) => ({
      id: inv.id,
      amount: inv.amount_paid,
      status: inv.status,
      createdAt: inv.created_at,
      invoiceUrl: inv.hosted_invoice_url,
      invoicePdf: inv.invoice_pdf
    }))
  }

  /**
   * Check if organization has reached submission limit
   */
  async checkSubmissionLimit(): Promise<{ allowed: boolean; current: number; limit: number }> {
    const subscription = await this.getSubscriptionStatus()
    const tierLimits = PRICING_TIERS[subscription.tier].limits

    if (tierLimits.submissions === -1) {
      return { allowed: true, current: 0, limit: -1 }
    }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { allowed: false, current: 0, limit: tierLimits.submissions }

    const { data: profile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single()

    if (!profile?.organization_id) {
      return { allowed: false, current: 0, limit: tierLimits.submissions }
    }

    const { count } = await supabase
      .from('submissions')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', profile.organization_id)

    const current = count || 0
    return {
      allowed: current < tierLimits.submissions,
      current,
      limit: tierLimits.submissions
    }
  }
}

export const stripeService = new StripeService()
